import {
  ChevronRight,
  Globe,
  MessageSquareText,
  PhoneCall,
  TriangleAlert,
} from 'lucide-react'
import type { InteractionOperationalState } from '../../conversations/interaction-operational-contracts'
import {
  appointmentStatusLabel,
  appointmentStatusTone,
  channelLabel,
  formatPhone,
  formatShortDate,
  formatShortDateTime,
  patientInitials,
  type EngagementChannel,
} from '../patient-display'
import type { PatientListItem } from '../patients-model'
import {
  nextPatientAppointment,
  patientNeedsAttention,
} from '../patients-model'
import styles from '../PatientsPage.module.css'

function ChannelIcon({ channel }: { channel: EngagementChannel }) {
  const Icon =
    channel === 'VOICE'
      ? PhoneCall
      : channel === 'WEB_WIDGET'
        ? Globe
        : MessageSquareText
  return <Icon size={13} aria-hidden="true" />
}

export function PatientRow({
  patient,
  operationalState,
  onSelect,
  onSendSms,
}: {
  patient: PatientListItem
  operationalState?: InteractionOperationalState
  onSelect: () => void
  onSendSms?: () => void
}) {
  const next = nextPatientAppointment(patient)
  const needsAttention = patientNeedsAttention(
    patient,
    operationalState && patient.interactionAggregateId
      ? { [patient.interactionAggregateId]: operationalState }
      : {}
  )
  const smsCapability = patient.smsContactCapability
  const smsUnavailableLabel =
    smsCapability?.reason === 'NO_PHONE'
      ? 'No phone number'
      : smsCapability?.reason === 'OPTED_OUT'
        ? 'Patient has opted out'
        : smsCapability?.reason === 'SHARED_PHONE' ||
            smsCapability?.reason === 'CONTACT_UNRESOLVED'
          ? 'Patient contact could not be resolved'
          : 'SMS unavailable for this clinic'
  const smsEnabled = smsCapability?.available === true && Boolean(onSendSms)
  const engagement = patient.latestEngagement
  return (
    <li
      className={styles.patientRow}
      data-attention={needsAttention || undefined}
    >
      <button
        type="button"
        className={styles.rowOpenButton}
        onClick={onSelect}
        aria-label={`Open ${patient.displayName}`}
      />
      <div className={styles.patientCell}>
        <span
          className={`${styles.avatar} ${patient.recordStatus === 'NEW' ? styles.newAvatar : ''}`}
          aria-hidden="true"
        >
          {patientInitials(patient.displayName)}
        </span>
        <div className={styles.patientNameBlock}>
          <div className={styles.patientNameLine}>
            <strong>{patient.displayName}</strong>
            {patient.recordStatus === 'NEW' ? (
              <span className={styles.newChip}>New</span>
            ) : null}
          </div>
          <span className={styles.practiceStatus}>
            Practice ID {patient.providerPatientId}
            {patient.source === 'FLIO_CREATED' ? ' · Added by Flio' : ''}
          </span>
        </div>
      </div>
      <div className={styles.contactCell}>
        {patient.phone ? (
          <span className={styles.cellPrimary}>
            {formatPhone(patient.phone)}
          </span>
        ) : (
          <span className={styles.mutedCell}>No phone on file</span>
        )}
        {patient.email ? <small>{patient.email}</small> : null}
      </div>
      <div className={styles.visitCell}>
        {next?.scheduledAtMs ? (
          <>
            <span className={styles.cellPrimary}>
              {formatShortDateTime(next.scheduledAtMs)}
            </span>
            <small>
              {next.appointmentTypeName ?? 'Appointment'}
              <span
                className={styles.statusText}
                data-tone={appointmentStatusTone(next.status)}
              >
                {appointmentStatusLabel(next.status)}
              </span>
            </small>
          </>
        ) : (
          <span className={styles.mutedCell}>No upcoming visit</span>
        )}
      </div>
      <div className={styles.interactionCell}>
        {needsAttention ? (
          <span className={styles.attentionChip}>
            <TriangleAlert size={12} aria-hidden="true" /> Needs attention
          </span>
        ) : null}
        {engagement ? (
          <>
            <span className={styles.channelLine}>
              <ChannelIcon channel={engagement.latestChannel} />
              {channelLabel(engagement.latestChannel)} ·{' '}
              {formatShortDate(engagement.latestActivityAtMs)}
            </span>
            <small>{engagement.latestPreview}</small>
          </>
        ) : needsAttention ? null : (
          <span className={styles.mutedCell}>No recent Flio contact</span>
        )}
      </div>
      <div className={styles.rowActions}>
        <button
          type="button"
          className={styles.smsActionButton}
          aria-label={
            smsEnabled
              ? `Send text message to ${patient.displayName}`
              : `${smsUnavailableLabel} for ${patient.displayName}`
          }
          onClick={onSendSms}
          disabled={!smsEnabled}
          title={smsEnabled ? 'Send text message' : smsUnavailableLabel}
        >
          <MessageSquareText size={16} aria-hidden="true" />
        </button>
        <ChevronRight
          className={styles.rowChevron}
          size={17}
          aria-hidden="true"
        />
      </div>
    </li>
  )
}
